import React, { useEffect } from 'react'

import { useInView } from 'react-intersection-observer'

import { observer } from 'mobx-react-lite'

import type { MicroInsightCardProps } from '../interfaces'
import { useCopyStore, useEvent } from '../context/hooks'

export type ViewMoreMicroCardProps = MicroInsightCardProps

import { ANALYTICS_CLICK_EVENTS, ANALYTICS_VIEW_EVENTS } from '../constants/Analytics'
import { VIEW_THRESHOLD } from '../constants/App'
import MicroCardTemplate from '../MicroCardTemplate'
import { BeatMaterialIcon } from '../beaticons'

import { alpha, useTheme } from '@mui/material/styles'

export const ViewMoreMicroCard: React.FC<MicroInsightCardProps> = ({
  ariaLabel,
  elevation,
  heightOverrides,
  isActiveCard,
  onCardClick,
  posthog,
  widthOverrides,
  showIcon,
  variant,
}) => {
  const theme = useTheme()
  const { ref, inView } = useInView({
    threshold: VIEW_THRESHOLD,
  })

  // *************** Context functions *********************
  const { cardsCopy } = useCopyStore()
  const { onEvent } = useEvent()

  // ************** Use Effects Hooks **********************
  useEffect(() => {
    if (inView) {
      onEvent(ANALYTICS_VIEW_EVENTS.ON_VIEW_MORE_VIEWED)
      posthog?.capture('viewed_insight', {
        template: 'view_more_card',
      })
    }
  }, [inView])

  // *************** Click handlers ************************
  const handleClick = () => {
    if (onCardClick) onCardClick()
    onEvent(ANALYTICS_CLICK_EVENTS.ON_VIEW_MORE_CLICKED)
    posthog?.capture('clicked_insight', {
      template: 'view_more_card',
    })
  }

  return (
    <MicroCardTemplate
      aria-roledescription="slide"
      ariaLabel={ariaLabel}
      description={cardsCopy?.view_more_card_description}
      elevation={elevation}
      heightOverrides={heightOverrides}
      icon={
        showIcon && (
          <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="show_chart" />
        )
      }
      isActiveCard={isActiveCard}
      onCardClick={handleClick}
      onPrimaryAction={handleClick}
      primaryActionText={cardsCopy?.micro_card_cta}
      ref={ref}
      title={cardsCopy?.view_more_card_title}
      variant={variant}
      widthOverrides={widthOverrides}
    />
  )
}

export default observer(ViewMoreMicroCard)
